import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Info, TrendingUp } from 'lucide-react';
import useFrictionMetrics from '../../hooks/useFrictionMetrics';

interface Props {
  processMapId: number;
  processMapName?: string;
}

type SortKey = 'score' | 'name' | 'error' | 'effort' | 'time' | 'handoff' | 'exception';

const LEVEL_CONFIG = {
  critical: { label: 'Critical', color: '#f56565', bg: '#fff5f5', text: '#742a2a' },
  high:     { label: 'High',     color: '#ed8936', bg: '#fffaf0', text: '#7b341e' },
  moderate: { label: 'Moderate', color: '#ecc94b', bg: '#fffff0', text: '#744210' },
  low:      { label: 'Low',      color: '#48bb78', bg: '#f0fff4', text: '#1c4532' },
};

const DIMENSIONS = [
  { key: 'error',     label: 'Error Rate',    scoreKey: 'error'     },
  { key: 'effort',    label: 'Manual Effort', scoreKey: 'effort'    },
  { key: 'time',      label: 'Cycle Time',    scoreKey: 'time'      },
  { key: 'handoff',   label: 'Handoffs',      scoreKey: 'handoff'   },
  { key: 'exception', label: 'Exceptions',    scoreKey: 'exception' },
] as const;

function scoreToColor(score: number): string {
  if (score >= 76) return '#f56565';
  if (score >= 51) return '#ed8936';
  if (score >= 26) return '#ecc94b';
  return '#48bb78';
}

function scoreToTextColor(score: number): string {
  if (score >= 26) return '#1a202c';
  return '#1a202c';
}

function scoreToBg(score: number): string {
  if (score >= 76) return '#fff5f5';
  if (score >= 51) return '#fffaf0';
  if (score >= 26) return '#fffff0';
  return '#f0fff4';
}

function truncate(s: string, max: number): string {
  return s && s.length > max ? s.slice(0, max - 1) + '…' : (s || '');
}

const FrictionHeatmapChart: React.FC<Props> = ({ processMapId, processMapName }) => {
  const { frictionData, isLoading, error, fetchFrictionData } = useFrictionMetrics();
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDesc, setSortDesc] = useState(true);

  React.useEffect(() => {
    if (processMapId) {
      fetchFrictionData(processMapId);
    }
  }, [processMapId]);

  const steps = useMemo(() => {
    const raw = (frictionData?.steps || []).filter((s: any) => s.has_data);
    return [...raw].sort((a: any, b: any) => {
      let va = 0, vb = 0;
      if (sortKey === 'score') { va = a.friction_score || 0; vb = b.friction_score || 0; }
      else if (sortKey === 'name') { return sortDesc ? b.step_name?.localeCompare(a.step_name) : a.step_name?.localeCompare(b.step_name); }
      else {
        va = a.normalized_scores?.[sortKey] || 0;
        vb = b.normalized_scores?.[sortKey] || 0;
      }
      return sortDesc ? vb - va : va - vb;
    });
  }, [frictionData, sortKey, sortDesc]);

  const summary = frictionData?.summary;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(d => !d);
    else { setSortKey(key); setSortDesc(true); }
  };

  const SortIndicator = ({ k }: { k: SortKey }) =>
    sortKey === k ? <span className="ml-0.5">{sortDesc ? '↓' : '↑'}</span> : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <div className="animate-spin w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full mr-3" />
        <span className="text-sm">Loading friction data…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>Failed to load friction data: {error}</span>
      </div>
    );
  }

  if (!frictionData || steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Info className="w-10 h-10 mb-3 text-gray-300" />
        <p className="font-medium text-gray-500">No friction data available</p>
        <p className="text-sm text-gray-400 mt-1">
          Use "Estimate Friction" or upload baseline metrics to populate the heatmap.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-red-500" />
          <span className="font-bold text-gray-800">
            Process Friction Heatmap
            {processMapName && <span className="text-gray-400 font-normal ml-2">· {processMapName}</span>}
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>{steps.length} steps with data</span>
          {summary && (
            <span className="font-semibold text-gray-700">
              Avg: <span style={{ color: scoreToColor(summary.average_friction) }}>
                {summary.average_friction.toFixed(1)}
              </span> / 100
            </span>
          )}
        </div>
      </div>

      {/* ── Summary KPI strip ── */}
      {summary && (
        <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
          {([
            { label: 'Critical', count: summary.critical_count, level: 'critical' },
            { label: 'High',     count: summary.high_count,     level: 'high'     },
            { label: 'Moderate', count: summary.moderate_count, level: 'moderate' },
            { label: 'Low',      count: summary.low_count,      level: 'low'      },
          ] as const).map(({ label, count, level }) => {
            const cfg = LEVEL_CONFIG[level];
            return (
              <div key={level} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
                  <span className="text-xs text-gray-600">{label}</span>
                </div>
                <span className="text-lg font-bold" style={{ color: cfg.color }}>{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Heatmap Grid ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th
                className="text-left px-4 py-2.5 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors w-48 sticky left-0 bg-gray-50 z-10"
                onClick={() => handleSort('name')}
              >
                Step Name <SortIndicator k="name" />
              </th>
              {DIMENSIONS.map(d => (
                <th
                  key={d.key}
                  className="text-center px-3 py-2.5 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors min-w-[90px]"
                  onClick={() => handleSort(d.key as SortKey)}
                >
                  {d.label} <SortIndicator k={d.key as SortKey} />
                </th>
              ))}
              <th
                className="text-center px-3 py-2.5 font-bold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors min-w-[90px]"
                onClick={() => handleSort('score')}
              >
                Total Score <SortIndicator k="score" />
              </th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step: any, idx: number) => {
              const ns = step.normalized_scores || {};
              const totalScore = step.friction_score || 0;
              const isEven = idx % 2 === 0;
              return (
                <tr
                  key={step.step_identifier || idx}
                  className={`border-b border-gray-100 hover:brightness-95 transition-all ${isEven ? 'bg-white' : 'bg-gray-50/50'}`}
                >
                  {/* Step name cell */}
                  <td className={`px-4 py-2 sticky left-0 z-10 ${isEven ? 'bg-white' : 'bg-gray-50'} border-r border-gray-100`}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-1.5 h-8 rounded-full flex-shrink-0"
                        style={{ background: step.friction_color || scoreToColor(totalScore) }}
                      />
                      <div>
                        <div className="font-semibold text-gray-800 leading-tight">
                          {truncate(step.step_name || step.step_identifier || `Step ${idx + 1}`, 28)}
                        </div>
                        {step.data_source && (
                          <div className={`text-xs mt-0.5 ${step.data_source === 'grounded' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {step.data_source === 'grounded' ? '✓ Grounded' : '~ Estimated'}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Dimension cells */}
                  {DIMENSIONS.map(d => {
                    const score = ns[d.scoreKey] ?? null;
                    return (
                      <td key={d.key} className="px-2 py-1.5 text-center">
                        {score != null ? (
                          <div
                            className="mx-auto rounded-md px-2 py-1.5 font-bold text-xs leading-none"
                            style={{
                              background: scoreToBg(score),
                              color: scoreToColor(score),
                              border: `1px solid ${scoreToColor(score)}40`,
                              minWidth: '44px',
                              display: 'inline-block'
                            }}
                          >
                            {score.toFixed(0)}
                          </div>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    );
                  })}

                  {/* Total score cell */}
                  <td className="px-2 py-1.5 text-center">
                    <div
                      className="mx-auto rounded-md px-2 py-1.5 font-bold text-sm leading-none"
                      style={{
                        background: scoreToColor(totalScore),
                        color: 'white',
                        minWidth: '52px',
                        display: 'inline-block'
                      }}
                    >
                      {totalScore.toFixed(0)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-6 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Score Legend:</span>
        {Object.entries(LEVEL_CONFIG).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ background: cfg.color }} />
            <span className="text-xs text-gray-600">
              <strong style={{ color: cfg.color }}>{cfg.label}</strong>
              {key === 'critical' && ' (76–100)'}
              {key === 'high'     && ' (51–75)'}
              {key === 'moderate' && ' (26–50)'}
              {key === 'low'      && ' (0–25)'}
            </span>
          </div>
        ))}
        <span className="text-xs text-gray-400 ml-auto">Click column headers to sort</span>
      </div>
    </div>
  );
};

export default FrictionHeatmapChart;
